import React, { useEffect, useMemo, useState } from "react";
import { listProfilesForLeaderboard } from "../api/supabaseData";

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function buildRows(rows) {
  return rows.map((row) => ({
    uid: row.id,
    name: row.display_name || "Hunter",
    xp: Number(row.total_xp || 0),
    level: Number(row.level || 1),
    title: row.current_title || "",
    photoURL: row.photo_url || "",
  }));
}

function rankRows(rows) {
  // Sort by XP desc, then level desc, then name asc
  const sorted = [...rows].sort((a, b) => {
    if (b.xp !== a.xp) return b.xp - a.xp;
    if (b.level !== a.level) return b.level - a.level;
    return (a.name || "").localeCompare(b.name || "");
  });
  return sorted;
}

function Section({ title, rows, meUid }) {
  const top10 = rows.slice(0, 10);
  const myIndex = rows.findIndex((r) => r.uid === meUid);
  const showMine = myIndex >= 10 && rows[myIndex];

  return (
    <div className="stats-card leaderboard-card">
      <h4>{title}</h4>

      <div className="lb-wrapper">
        <table className="leaderboard-table">
          <thead>
            <tr className="lb-head">
              <th style={{ width: 70 }}>Pos</th>
              <th>User</th>
              <th style={{ width: 80, textAlign: "right" }}>Level</th>
              <th style={{ width: 210 }}>Title</th>
              <th style={{ width: 100, textAlign: "right" }}>XP</th>
            </tr>
          </thead>
          <tbody>
            {top10.map((r, i) => (
              <tr key={r.uid} className={`lb-row ${r.uid === meUid ? "me-row" : ""}`}>
                <td>{ordinal(i + 1)}</td>
                <td className="lb-user">
                  <span>{r.name}</span>
                </td>
                <td style={{ textAlign: "right" }}>{r.level}</td>
                <td className="lb-title">{r.title}</td>
                <td style={{ textAlign: "right" }}>{r.xp.toLocaleString()}</td>
              </tr>
            ))}

            {showMine && (
              <>
                <tr className="lb-ellipsis">
                  <td colSpan={5}>…</td>
                </tr>
                <tr key={rows[myIndex].uid} className="lb-row me-row">
                  <td>{ordinal(myIndex + 1)}</td>
                  <td className="lb-user">
                    {rows[myIndex].photoURL ? (
                      <img src={rows[myIndex].photoURL} alt="" className="lb-avatar" />
                    ) : (
                      <span className="lb-avatar placeholder">👤</span>
                    )}
                    <span>{rows[myIndex].name}</span>
                  </td>
                  <td style={{ textAlign: "right" }}>{rows[myIndex].level}</td>
                  <td className="lb-title">{rows[myIndex].title}</td>
                  <td style={{ textAlign: "right" }}>{rows[myIndex].xp.toLocaleString()}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Leaderboard = ({ user }) => {
  const [allRows, setAllRows] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const rows = buildRows(await listProfilesForLeaderboard());
        if (!mounted) return;
        setAllRows(rankRows(rows));

        const mine = rows.find((r) => r.uid === user?.uid) || null;
        setMe(mine);
      } catch (e) {
        console.error("Leaderboard load error:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => (mounted = false);
  }, [user?.uid]);

  const overallRows = allRows;
  const rankRowsOnly = useMemo(() => {
    if (!me) return [];
    // Same rank means same title (fallback to same level)
    const same = allRows.filter(
      (r) => (me.title && r.title === me.title) || (!me.title && r.level === me.level)
    );
    return rankRows(same);
  }, [allRows, me]);

  if (loading) return <div className="stats-card">Loading leaderboard…</div>;

  return (
    <div className="leaderboard-page">
      <Section title="Global Leaderboard" rows={overallRows} meUid={user?.uid} />
      <Section
        title={`Rank Leaderboard — ${me?.title || `Level ${me?.level || ""}`}`}
        rows={rankRowsOnly}
        meUid={user?.uid}
      />
    </div>
  );
};

export default Leaderboard;
